import { useCallback, useEffect, useState } from 'react'
import { NextStepModal } from '../nextStep'
import MyCareerPlanTable from './careerPlanTable'
import areasMapper from './../../../Utils/AreasMapper'
import levelsMapper from './../../../Utils/LevelsMapper'
import subareasMapper from './../../../Utils/SubareasMapper.js'
import List from '../../../API/Lists/Lists.js'
import Loading from '../../Loading/Loading'
import CareerApi from '../../../API/career/Career.api'
import { DeleteModal } from '../DeleteModal'
import { useIntl } from 'react-intl'
import { myCareerPlanTranslate } from '../../../i18n/translatedMessages'

const SuccessModal = ({ message, onClose }) => {
	const handleOverlayClick = e => {
		if (e.target === e.currentTarget) {
			onClose()
		}
	}

	return (
		<div onClick={handleOverlayClick} className='tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center'>
			<div className='tw-bg-white tw-p-6 tw-rounded-lg tw-w-8/12 tw-shadow-lg tw-text-center tw-relative'>
				<button onClick={onClose} className='tw-absolute tw-top-2 tw-right-2 tw-text-gray-500 hover:tw-text-gray-800'>
					&times;
				</button>
				<h4 className='tw-font-bold tw-text-green-500'>{message}</h4>
			</div>
		</div>
	)
}

export function MyCareerPlan() {
	const getLanguage = useIntl()
	const language = getLanguage.locale === 'PT-BR' ? 'pt' : 'es'

	const employee_id = JSON.parse(localStorage.getItem('user')).id
	const employee_level = JSON.parse(localStorage.getItem('employee_level'))

	const [modalOpen, setModalOpen] = useState(false)
	const [deleteModalOpen, setDeleteModalOpen] = useState(false)
	const [deleteIndex, setDeleteIndex] = useState(null)

	const [loading, setLoading] = useState(false)

	const [lists, setLists] = useState({
		areas: [],
		levels: [],
		subareas: [],
	})
	const [careers, setCareers] = useState([])

	const [selectedData, setSelectedData] = useState({
		id: '',
		status: '',
		level_id: '',
		area_id: '',
		subarea_id: '',
	})

	const load = useCallback(async () => {
		try {
			setLoading(true)

			const [resArea, resLevel, resSubareas, resCareersEmployee] = await Promise.all([
				List.areas(),
				List.levels(),
				List.subareas(),
				CareerApi.getAllCareerEmployee(employee_id),
			])

			const areas = resArea.map(areasMapper.toDomain)
			const subareas = resSubareas.map(subareasMapper.toDomain)
			const careers = resCareersEmployee.filter(career => career.chosen_by === 'employee')
			const filteredLevels = resLevel.map(levelsMapper.toDomain).filter(level => level.id >= employee_level)

			setLists({ areas, levels: filteredLevels, subareas })

			setCareers([...careers])
		} catch {
		} finally {
			setLoading(false)
		}
	}, [])

	useEffect(() => {
		load()
	}, [load])

	const handleAdd = () => {
		setSelectedData(null)
		setModalOpen(true)
	}

	const closeModal = async () => {
		setModalOpen(false)
	}

	const sendData = async data => {
		try {
			let newData
			if (selectedData) {
				newData = { 'level_id': data.level_id, 'area_id': data.area_id, 'subarea_id': data.subarea }
				await CareerApi.updateCareerEmployee(selectedData.id, newData)
				setCareers(prev => prev.map(c => (c.id === selectedData.id ? { ...c, ...newData } : c)))
			} else {
				newData = {
					'employee_id': employee_id,
					'level_id': data.level_id,
					'area_id': data.area_id,
					'subarea_id': data.subarea,
					'chosen_by': 'employee',
				}

				const newCareer = await CareerApi.addNewCareerEmployee(newData)

				setCareers(prev => [...prev, newCareer])
			}

			setModalOpen(false)
		} catch (error) {}
	}

	const deleteData = async id => {
		try {
			await CareerApi.deleteCareerEmployee(id)
			setCareers(prev => prev.filter(career => career.id !== id))
		} catch (error) {}
	}

	const handleDeleteRequest = id => {
		setDeleteIndex(id)
		setDeleteModalOpen(true)
	}

	const confirmDelete = () => {
		deleteData(deleteIndex)
		setDeleteModalOpen(false)
	}

	return (
		<>
			<div className='tw-mb-6 tw-flex tw-justify-between tw-items-center'>
				<div>
					<h4 className='tw-font-bold'>{myCareerPlanTranslate.myCareerPlanTitle}</h4>
					<div>{myCareerPlanTranslate.myCareerPlanDesc}</div>
				</div>
				<button className='tw-bg-purple-500 tw-border-none tw-text-white tw-px-4 tw-py-1 tw-rounded-xl' onClick={handleAdd}>
					+ {myCareerPlanTranslate.myCareerPlanAddBtn}
				</button>
			</div>

			{loading ? (
				<Loading />
			) : (
				<>
					{modalOpen && <NextStepModal onClose={closeModal} onSave={sendData} data={selectedData} lists={lists} language={language} />}

					<DeleteModal
						show={deleteModalOpen}
						onClick={confirmDelete}
						close={() => setDeleteModalOpen(false)}
						title={myCareerPlanTranslate.myCareerPlanConfirmDelete}
						item={deleteIndex}
					/>

					<MyCareerPlanTable
						data={careers}
						lists={lists}
						setModalOpen={setModalOpen}
						setSelectedData={setSelectedData}
						onDelete={handleDeleteRequest}
						employee_level={employee_level}
					/>
				</>
			)}
		</>
	)
}
